import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "./Reveal";
import { useLinks } from "../lib/LinksContext";
import { useContent } from "../lib/ContentContext";
import { SOCIAL_LINKS } from "../lib/socials";

const LINKS = [
  { to: "/eugenie", k: "nav.eugenie", id: "nav-link-eugenie" },
  { to: "/clinique", k: "nav.clinique", id: "nav-link-clinique" },
  { to: "/conversations", k: "nav.conversations", id: "nav-link-conversations" },
  { to: "/medias", k: "nav.medias", id: "nav-link-medias" },
  { to: "/conferences", k: "nav.conferences", id: "nav-link-conferences" },
  { to: "/projets", k: "nav.projets", id: "nav-link-projets" },
  { to: "/contact", k: "nav.contact", id: "nav-link-contact" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const links = useLinks();
  const c = useContent();
  const socials = SOCIAL_LINKS.filter((s) => links[s.key]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-navigation"
        className="fixed inset-x-0 top-0 z-50 border-b border-linen/70 bg-cream/70 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 md:px-12">
          <Link
            to="/"
            data-testid="nav-wordmark"
            className="block transition-opacity duration-300 hover:opacity-70"
          >
            <img src="/logo-name.png" alt="Eugénie Larrivée" className="h-9 w-auto" />
          </Link>

          <nav className="hidden items-center gap-6 xl:flex" aria-label="Navigation principale">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `group relative text-xs font-medium uppercase tracking-[0.16em] transition-colors duration-300 ${
                    isActive ? "text-clay" : "text-smoke hover:text-charcoal"
                  }`
                }
              >
                {c(l.k)}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-clay transition-[width] duration-300 group-hover:w-full" />
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-4 xl:flex">
            {socials.map(({ key, label, Icon }) => (
              <a
                key={key}
                href={links[key]}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`nav-social-${key}`}
                className="text-ash transition-colors duration-300 hover:text-clay"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-parler"
              className="group ml-2 inline-flex items-center gap-2 rounded-full bg-clay px-5 py-2.5 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal"
            >
              {c("nav.cta")}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            data-testid="nav-menu-toggle"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-linen text-charcoal transition-colors duration-300 hover:border-clay hover:text-clay xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-40 flex flex-col justify-between bg-cream px-6 pb-10 pt-28"
          >
            <nav className="flex flex-col gap-2" aria-label="Navigation mobile">
              {[{ to: "/", k: "nav.accueil", id: "nav-link-accueil" }, ...LINKS].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE, delay: 0.07 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`${l.id}-mobile`}
                    className={({ isActive }) =>
                      `block border-b border-linen py-3.5 font-serif text-3xl font-light tracking-tight transition-colors duration-300 ${
                        isActive ? "text-clay" : "text-charcoal"
                      }`
                    }
                  >
                    {c(l.k)}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-between"
            >
              <Link
                to="/contact"
                data-testid="nav-cta-parler-mobile"
                className="inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-cream"
              >
                {c("nav.cta")}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
              <div className="flex items-center gap-4">
                {socials.map(({ key, label, Icon }) => (
                  <a key={key} href={links[key]} target="_blank" rel="noopener noreferrer"
                    aria-label={label} data-testid={`nav-social-${key}-mobile`} className="text-smoke">
                    <Icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
